/**
 * Accepting an invitation: choose a password, and you are in.
 *
 * The token arrives in the URL *fragment* rather than the path, because a
 * fragment is never sent to the server — it stays out of access logs, proxies
 * and referrer headers. This page reads it and posts it in a body instead.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Lockup } from "@/components/Logo";
import { api, ApiError } from "@/lib/api";
import { ADMIN_PERMISSIONS, useSession } from "@/lib/session";

interface Preview {
  email: string;
  display_name: string | null;
  organization_name: string | null;
  expires_at: string;
}

function tokenFromUrl(): string {
  const fragment = window.location.hash.replace(/^#/, "");
  return new URLSearchParams(fragment).get("token") ?? "";
}

export function AcceptInvitation() {
  const navigate = useNavigate();
  const { adoptToken } = useSession();

  const [token] = useState(tokenFromUrl);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [checking, setChecking] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setProblem("That link is incomplete. Ask your administrator to send another.");
      setChecking(false);
      return;
    }
    let cancelled = false;
    api.post<Preview>("/auth/invitation", { token })
      .then((body) => {
        if (cancelled) return;
        setPreview(body);
        setDisplayName(body.display_name ?? "");
      })
      .catch((caught) => {
        if (cancelled) return;
        setProblem(
          caught instanceof ApiError ? caught.message : "That link is not valid.",
        );
      })
      .finally(() => !cancelled && setChecking(false));
    return () => { cancelled = true; };
  }, [token]);

  const submit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setError("Those two passwords are not the same.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = await api.post<{
        access_token: string;
        user: { permissions?: string[] };
      }>(
        "/auth/invitation/accept",
        { token, password, display_name: displayName || null },
      );
      // Clear the fragment before anything else can read it, and leave no
      // history entry holding a credential.
      window.history.replaceState(null, "", window.location.pathname);
      await adoptToken(body.access_token);
      // The first person in an organisation was invited to administer it, and
      // landing them in the employee portal hides the very thing they were
      // invited to do behind a menu they have no reason to open. Anyone whose
      // invitation carried no administrative permission still starts where
      // everybody else does.
      const admin = (body.user?.permissions ?? []).some(
        (permission) => ADMIN_PERMISSIONS.includes(permission),
      );
      navigate(admin ? "/admin" : "/", { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "That did not work.");
      setBusy(false);
    }
  }, [token, password, confirm, displayName, adoptToken, navigate]);

  if (checking) {
    return (
      <div className="page page--centred">
        <p className="ink-tertiary">Checking your invitation…</p>
      </div>
    );
  }

  if (problem) {
    return (
      <div className="page page--centred">
        <div className="card auth-card">
          <div className="card__body stack" style={{ gap: "var(--space-5)" }}>
            <Lockup height={30} />
            <div className="stack" style={{ gap: 4 }}>
              <h1 className="t-h2">This invitation cannot be used</h1>
              <p className="t-body ink-secondary">{problem}</p>
            </div>
            <button className="btn btn--secondary" onClick={() => navigate("/sign-in")}>
              Go to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tooShort = password.length > 0 && password.length < 12;

  return (
    <div className="page page--centred">
      <form className="card auth-card" onSubmit={submit}>
        <div className="card__body stack" style={{ gap: "var(--space-5)" }}>
          <Lockup height={30} />
          <div className="stack" style={{ gap: 4 }}>
            <h1 className="t-h2">
              Welcome to {preview?.organization_name ?? "AlphaRoom"}
            </h1>
            <p className="t-body ink-secondary">
              Choose a password for <strong>{preview?.email}</strong> and you are
              ready to book rooms.
            </p>
          </div>

          {error && <div className="notice notice--error">{error}</div>}

          <div className="field">
            <label className="field__label" htmlFor="name">Your name</label>
            <input id="name" className="input" value={displayName}
              autoComplete="name"
              onChange={(event) => setDisplayName(event.target.value)} />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="password">Password</label>
            <input id="password" className="input" type="password"
              autoComplete="new-password" value={password}
              onChange={(event) => setPassword(event.target.value)} required />
            <span className={tooShort ? "field__error" : "field__hint"}>
              At least 12 characters.
            </span>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="confirm">Confirm password</label>
            <input id="confirm" className="input" type="password"
              autoComplete="new-password" value={confirm}
              onChange={(event) => setConfirm(event.target.value)} required />
          </div>

          <button className="btn btn--primary"
            disabled={busy || password.length < 12 || !confirm}>
            {busy ? "Setting up…" : "Set password and sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}
